import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { generateTemplates } from '../../../services/communicationAIService';

const TemplatesModal = ({ isOpen, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState('property_inquiry');
  const [customCategory, setCustomCategory] = useState('');
  const [templates, setTemplates] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const categoryOptions = [
    { value: 'property_inquiry', label: 'استفسارات عقارية' },
    { value: 'viewing_confirmation', label: 'تأكيد معاينات' },
    { value: 'follow_up', label: 'متابعة عملاء' },
    { value: 'offer_notification', label: 'إشعارات عروض' },
    { value: 'contract_reminder', label: 'تذكيرات عقود' },
    { value: 'payment_reminder', label: 'تذكيرات دفع' },
    { value: 'welcome_message', label: 'رسائل ترحيب' },
    { value: 'thank_you', label: 'رسائل شكر' },
    { value: 'custom', label: 'فئة مخصصة' },
  ];

  const handleGenerateTemplates = async () => {
    const category = selectedCategory === 'custom' ? customCategory : selectedCategory;
    
    if (!category) {
      alert('الرجاء اختيار أو إدخال فئة');
      return;
    }

    setIsGenerating(true);
    try {
      const generatedTemplates = await generateTemplates({
        category,
        count: 5,
        tone: 'professional',
      });
      setTemplates(generatedTemplates);
    } catch (error) {
      alert(error?.message || 'فشل في توليد القوالب');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseTemplate = (template) => {
    setSelectedTemplate(template);
    // Copy to clipboard
    navigator.clipboard?.writeText(template?.template);
    alert('تم نسخ القالب إلى الحافظة!');
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="قوالب الرسائل" 
      size="lg"
      footer={null}
      icon={null}
      iconColor={null}
    >
      <div className="space-y-6" dir="rtl">
        {/* Category Selection */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              فئة القوالب
            </label>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e?.target?.value)}
              options={categoryOptions}
            />
          </div>

          {selectedCategory === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                أدخل الفئة المخصصة
              </label>
              <Input
                value={customCategory}
                onChange={(e) => setCustomCategory(e?.target?.value)}
                placeholder="مثال: دعوات فعاليات، عروض خاصة، إلخ"
              />
            </div>
          )}

          <Button
            onClick={handleGenerateTemplates}
            disabled={isGenerating || (selectedCategory === 'custom' && !customCategory)}
            className="w-full"
          >
            {isGenerating ? (
              <>
                <Icon name="Loader" size={18} className="animate-spin" />
                <span>جاري التوليد...</span>
              </>
            ) : (
              <>
                <Icon name="Sparkles" size={18} />
                <span>توليد قوالب بالذكاء الاصطناعي</span>
              </>
            )}
          </Button>
        </div>

        {/* Templates Display */}
        {templates?.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="FileText" size={20} color="#3B82F6" />
              <span className="text-lg font-semibold text-blue-400">
                القوالب المولدة ({templates?.length})
              </span>
            </div>

            <div className="max-h-[500px] overflow-y-auto space-y-3">
              {templates?.map((template, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                    selectedTemplate?.title === template?.title
                      ? 'bg-gradient-to-br from-blue-900/40 to-purple-900/40 border-blue-500' :'bg-gray-800/30 border-gray-700 hover:border-gray-600'
                  }`}
                  onClick={() => setSelectedTemplate(template)}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">{template?.title}</h4>
                      <div className="flex flex-wrap gap-2">
                        {template?.placeholders?.map((placeholder, idx) => (
                          <span
                            key={idx}
                            className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded"
                          >
                            {placeholder}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Button
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleUseTemplate(template);
                      }}
                      size="sm"
                      variant="secondary"
                    >
                      <Icon name="Copy" size={16} />
                      <span>نسخ</span>
                    </Button>
                  </div>

                  <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700">
                    <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">
                      {template?.template}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {templates?.length === 0 && !isGenerating && (
          <div className="text-center py-12">
            <Icon name="FileText" size={48} color="#6B7280" className="mx-auto mb-4" />
            <p className="text-gray-400">
              اختر فئة واضغط على "توليد" للحصول على قوالب مخصصة
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-gray-700">
          <Button onClick={onClose} variant="secondary" className="flex-1">
            إغلاق
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TemplatesModal;